import { Box, HStack, Text } from '@chakra-ui/react';
import { Suspense, useCallback, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { Canvas, useThree } from '@react-three/fiber';
import { useAppStore } from '../../state/store';
import { useBIMStore } from '../../state/useBIMStore';
import { useQuadViewStore, type QuadDirection } from '../../state/useQuadViewStore';
import { EntityLayer } from '../../rendering/EntityLayer';
import { BIMShadedLayer } from '../../rendering/BIMShadedLayer';
import { SketchReferenceLayer } from '../../rendering/SketchReferenceLayer';
import { DemandRenderController } from '../../rendering/RenderLoopController';
import { PreviewViewportContext } from '../../rendering/toolEventBus';
import { collectDocumentBounds } from '../../rendering/documentBounds';
import { useViewportThemeColors } from '../../hooks/useThemeInk';
import { ViewportAxesGizmo } from './ViewportAxesGizmo';
import { QUAD_VIEW_DIRS, viewBasis } from './quadViewDirections';

/**
 * MiniViewport (R5): a LIVE orthographic viewport onto the shared document
 * (Top/Front/Right cell of the quad layout). Unlike the main interactive
 * viewport it mounts the document scene read-only inside its own canvas:
 * the same EntityLayer/BIM/Sketch content components render the shared
 * store, so every edit, selection highlight and display-mode change appears
 * here the moment it appears in Perspective — Rhino's synchronized quad
 * behavior.
 *
 * Each cell owns its camera framing (zoom + target) in useQuadViewStore, so
 * poses survive workbench switches. Wheel zooms, middle-drag pans, and a
 * plain left click picks through the shared selection router. Until the
 * user takes control (first zoom/pan) a cell keeps fitting itself to the
 * document extent, so new content is never "invisible" in the quad cells.
 */

/** Camera standoff for the orthographic pose (scales the near/far range). */
const POSE_DISTANCE = 5000;

/**
 * Ground plane of each cell: the coordinate plane the view looks straight
 * at (top/bottom → XY, front/back → YZ, right/left → XZ), expressed as a
 * gridHelper rotation — the helper lies in the XZ plane by default, so the
 * XY plane needs a +90° X rotation and the YZ plane a +90° Z rotation.
 */
const GRID_PLANE_ROTATION: Record<QuadDirection, [number, number, number]> = {
    top: [Math.PI / 2, 0, 0],
    bottom: [Math.PI / 2, 0, 0],
    front: [0, 0, Math.PI / 2],
    back: [0, 0, Math.PI / 2],
    right: [0, 0, 0],
    left: [0, 0, 0],
};

/** Camera rig: poses the ortho camera from the cell's stored view state. */
function OrthoViewRig({ direction }: { direction: QuadDirection }) {
    const camera = useThree((s) => s.camera) as THREE.OrthographicCamera;
    const size = useThree((s) => s.size);
    const invalidate = useThree((s) => s.invalidate);
    const autoFrame = useQuadViewStore((s) => s.cells[direction].autoFrame);

    /**
     * Fit the cell to the document extent (ZoomExtents semantics). Writes
     * only when the fit actually differs, so the auto-frame store feedback
     * loop (frame -> setCell -> subscribe -> frame) settles immediately.
     */
    const frame = useCallback(() => {
        const bounds = collectDocumentBounds();
        const center = bounds ? bounds.getCenter(new THREE.Vector3()) : new THREE.Vector3();
        const radius = bounds ? Math.max(bounds.getSize(new THREE.Vector3()).length() / 2, 0.5) : 10;
        const minSide = Math.max(Math.min(size.width, size.height), 1);
        const zoom = Math.max(minSide / (radius * 2.4), 0.01);
        const target: [number, number, number] = [center.x, center.y, center.z];
        const current = useQuadViewStore.getState().cells[direction];
        const moved = Math.hypot(
            target[0] - current.target[0],
            target[1] - current.target[1],
            target[2] - current.target[2]
        ) > radius * 0.01;
        const zoomed = Math.abs(zoom - current.zoom) / Math.max(current.zoom, 1e-6) > 0.01;
        if (moved || zoomed) useQuadViewStore.getState().setCell(direction, { zoom, target });
    }, [direction, size.width, size.height]);

    // First pose + window resize: an untouched cell always shows the extent.
    useEffect(() => {
        if (autoFrame) frame();
    }, [autoFrame, frame]);

    // Document growth: while the cell is untouched, keep following the
    // extent (rAF-coalesced so a burst of store writes costs one frame).
    useEffect(() => {
        if (!autoFrame) return;
        let scheduled = false;
        const schedule = () => {
            if (scheduled) return;
            scheduled = true;
            requestAnimationFrame(() => { scheduled = false; frame(); });
        };
        const unsubApp = useAppStore.subscribe(schedule);
        const unsubBim = useBIMStore.subscribe(schedule);
        return () => { unsubApp(); unsubBim(); };
    }, [autoFrame, frame]);

    // ZOOMEXTENTS chord: an explicit fit reframes every quad cell too.
    useEffect(() => {
        const fit = () => frame();
        window.addEventListener('yap:zoom-to-fit', fit);
        return () => window.removeEventListener('yap:zoom-to-fit', fit);
    }, [frame]);

    // Apply the stored pose (position/up/zoom) to the camera.
    const zoom = useQuadViewStore((s) => s.cells[direction].zoom);
    const target = useQuadViewStore((s) => s.cells[direction].target);
    useEffect(() => {
        const base = QUAD_VIEW_DIRS[direction];
        camera.up.set(base.up[0], base.up[1], base.up[2]);
        camera.position.set(
            target[0] + base.position[0] * POSE_DISTANCE,
            target[1] + base.position[1] * POSE_DISTANCE,
            target[2] + base.position[2] * POSE_DISTANCE
        );
        camera.near = 0.1;
        camera.far = POSE_DISTANCE * 4;
        camera.zoom = zoom;
        camera.lookAt(target[0], target[1], target[2]);
        camera.updateProjectionMatrix();
        invalidate();
    }, [camera, direction, zoom, target, invalidate]);

    return null;
}

/** Grid sized to the cell's current view width, drawn on the coordinate
    plane the cell looks straight at (canvas-internal). */
function GroundGrid({ zoom, rotation }: { zoom: number; rotation: [number, number, number] }) {
    const size = useThree((s) => s.size);
    const invalidate = useThree((s) => s.invalidate);
    // Grid tones resolve through the 3D workbench token sheet (quad cells
    // belong to the 3D workbench), including any Settings › Color overrides.
    const gridColors = useViewportThemeColors('3d');
    // gridHelper colors arrive as constructor args (a new helper per edit),
    // so an explicit frame keeps the demand loop in sync with editor edits.
    useEffect(() => { invalidate(); }, [invalidate, gridColors]);
    // Grid footprint follows the current view width so the lines always
    // cover the cell even deep zoomed out (finite helper, distance-honest).
    const gridSize = Math.max(
        20,
        Math.ceil((Math.max(size.width, size.height) / Math.max(zoom, 1e-6)) * 1.2 / 10) * 10
    );
    return (
        <gridHelper
            args={[gridSize, Math.min(gridSize, 240), gridColors.gridSection, gridColors.gridMajor]}
            rotation={rotation}
        />
    );
}

export function MiniViewport({ direction }: { direction: QuadDirection }) {
    const gridVisible = useAppStore((s) => s.view.gridVisible);
    const axesVisible = useAppStore((s) => s.view.axesVisible);
    const setCell = useQuadViewStore((s) => s.setCell);
    const zoom = useQuadViewStore((s) => s.cells[direction].zoom);

    const panState = useRef<{ x: number; y: number; target: [number, number, number] } | null>(null);

    const onWheel = (e: React.WheelEvent) => {
        const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
        const current = useQuadViewStore.getState().cells[direction];
        setCell(direction, {
            zoom: Math.min(Math.max(current.zoom * factor, 0.001), 100000),
            autoFrame: false,
        });
    };

    const onPointerDown = (e: React.PointerEvent) => {
        if (e.button !== 1) return; // middle button = pan
        e.preventDefault();
        panState.current = {
            x: e.clientX,
            y: e.clientY,
            target: [...useQuadViewStore.getState().cells[direction].target] as [number, number, number],
        };
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e: React.PointerEvent) => {
        const start = panState.current;
        if (!start) return;
        const zoomNow = useQuadViewStore.getState().cells[direction].zoom;
        const worldPerPx = 1 / Math.max(zoomNow, 1e-6);
        const { right, up } = viewBasis(direction);
        const dx = (e.clientX - start.x) * worldPerPx;
        const dy = (e.clientY - start.y) * worldPerPx;
        // Natural grab panning: the content follows the cursor, so the view
        // window moves opposite to the drag horizontally and along it
        // vertically (screen up is +world up on screen).
        setCell(direction, {
            target: [
                start.target[0] - right.x * dx + up.x * dy,
                start.target[1] - right.y * dx + up.y * dy,
                start.target[2] - right.z * dx + up.z * dy,
            ],
            autoFrame: false,
        });
    };

    const onPointerUp = () => { panState.current = null; };

    return (
        <Box
            w="100%"
            h="100%"
            position="relative"
            bg="var(--color-bg-viewport-3d)"
            onWheel={onWheel}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
        >
            <Canvas frameloop="demand" dpr={[1, 1.5]} orthographic camera={{ zoom, near: 0.1, far: POSE_DISTANCE * 4 }}>
                {/* Preview scope: the document mounts read-only here (mesh
                    registration stays owned by the interactive viewport). */}
                <PreviewViewportContext.Provider value={true}>
                    <OrthoViewRig direction={direction} />

                    {/* Demand loop parity with the main viewport: any store
                        change (entities, selection, display modes) repaints
                        this cell, keeping all views synchronized. */}
                    <DemandRenderController />

                    <ambientLight intensity={0.7} />
                    <directionalLight position={[300, -200, 500]} intensity={1.1} />

                    {gridVisible && <GroundGrid zoom={zoom} rotation={GRID_PLANE_ROTATION[direction]} />}

                    <Suspense fallback={null}>
                        <EntityLayer />
                        <BIMShadedLayer />
                        <SketchReferenceLayer shaded />
                    </Suspense>
                </PreviewViewportContext.Provider>
            </Canvas>
            <HStack position="absolute" top={1} left={2} spacing={2} pointerEvents="none">
                <Text fontSize="xs" color="text.secondary" fontWeight="bold" textTransform="uppercase">
                    {direction}
                </Text>
            </HStack>
            {/* Orientation triad (X/Y/Z) in the bottom-right corner; the
                fixed ortho direction keeps it static. Replaces the origin
                axes helper as the view-orientation cue. */}
            {axesVisible && <ViewportAxesGizmo direction={direction} backgroundToken="--color-bg-viewport-3d" />}
        </Box>
    );
}
